import { useRef } from "react";
import { createPortal } from "react-dom";
import { useDialogFocus } from "@/hooks/useDialogFocus";

type CheckinActionConfirmDialogProps = {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  pending?: boolean;
  confirmDisabled?: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: () => void;
};

const backdropStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 1100,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "1rem",
  background: "rgba(15, 23, 42, 0.42)",
  backdropFilter: "blur(2px)",
};

const dialogStyle: React.CSSProperties = {
  width: "min(92vw, 420px)",
  display: "flex",
  flexDirection: "column",
  gap: "0.9rem",
  padding: "1.1rem",
  borderRadius: "0.9rem",
  border: "1px solid color-mix(in srgb, var(--login-border) 82%, #ffffff 4%)",
  background: "var(--login-surface)",
  boxShadow: "0 20px 48px rgba(15, 23, 42, 0.18)",
};

const actionsStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "0.625rem",
};

const secondaryButtonStyle: React.CSSProperties = {
  minHeight: "40px",
  padding: "0 0.95rem",
  borderRadius: "999px",
  border: "1px solid var(--login-border)",
  background: "transparent",
  color: "var(--login-muted)",
  fontFamily: "'Barlow Condensed', sans-serif",
  fontWeight: 700,
  fontSize: "0.9rem",
  letterSpacing: "0.04em",
  cursor: "pointer",
};

const confirmButtonStyle: React.CSSProperties = {
  minHeight: "40px",
  padding: "0 1.05rem",
  borderRadius: "999px",
  border: "none",
  background: "var(--login-accent)",
  color: "#fff",
  fontFamily: "'Barlow Condensed', sans-serif",
  fontWeight: 700,
  fontSize: "0.9rem",
  letterSpacing: "0.04em",
  cursor: "pointer",
};

export default function CheckinActionConfirmDialog(
  props: CheckinActionConfirmDialogProps,
) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const close = () => {
    if (!props.pending) props.onCancel();
  };
  useDialogFocus(dialogRef, close, {
    enabled: props.open,
    initialFocusRef: cancelRef,
  });
  if (!props.open) return null;

  return createPortal(
    <div style={backdropStyle} onClick={close}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={props.title}
        tabIndex={-1}
        style={dialogStyle}
        onClick={(event) => event.stopPropagation()}
      >
        <div
          style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontWeight: 700,
            fontSize: "0.78rem",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "var(--login-muted)",
          }}
        >
          Confirmar ação
        </div>
        <h3
          style={{
            margin: 0,
            fontFamily: "'Barlow Condensed', sans-serif",
            fontWeight: 700,
            fontSize: "1.3rem",
            color: "var(--login-text)",
            lineHeight: 1.1,
          }}
        >
          {props.title}
        </h3>
        <p
          style={{
            margin: 0,
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "0.92rem",
            color: "var(--login-muted)",
            lineHeight: 1.5,
          }}
        >
          {props.description}
        </p>
        {props.error ? (
          <p role="alert" style={{ margin: 0, color: "var(--login-error)" }}>
            {props.error}
          </p>
        ) : null}
        <div style={actionsStyle}>
          <button
            ref={cancelRef}
            type="button"
            onClick={props.onCancel}
            disabled={props.pending}
            style={{
              ...secondaryButtonStyle,
              cursor: props.pending ? "not-allowed" : "pointer",
              opacity: props.pending ? 0.6 : 1,
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={props.onConfirm}
            disabled={props.pending || props.confirmDisabled}
            style={{
              ...confirmButtonStyle,
              cursor:
                props.pending || props.confirmDisabled
                  ? "not-allowed"
                  : "pointer",
              opacity: props.pending || props.confirmDisabled ? 0.7 : 1,
            }}
          >
            {props.confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
